import {
  Box,
  Card,
  Grid2 as Grid,
  useMediaQuery,
  Typography,
  TextField,
  Autocomplete,
} from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { useEffect, useMemo, useState } from "react";
import { useTheme } from "@emotion/react";
import ContractorCard from "../permitComponents/ContractorCard";
import { useDispatch, useSelector } from "react-redux";
import { countiesForLicense } from "../../data/dbpr";
import { LoadingButton } from "@mui/lab";

import { convertIsoToLocalFormat } from "../../functions/helperFunctions";
import axios from "axios";
import { fetchPrivateProviderData } from "../../store/actions/privateProviderActions";

const LicenseInfoSection = () => {
  const theme = useTheme();

  //? SCREEN SIZES ##################################################################
  const tabletSize = useMediaQuery(theme.breakpoints.down("md"));
  //? ###############################################################################

  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession].user);

  const [addContractorError, setAddContractorError] = useState("");
  const [addContractorLoading, setAddContractorLoading] = useState(false);

  const dispatch = useDispatch();

  // CONTRACTOR FORMIK ///
  const handlePhoneChange = (event) => {
    const { value } = event.target;

    if (value.length > 12) {
      return;
    }
    const digitsOnly = value.replace(/\D/g, ""); // Remove non-numeric characters
    let formattedPhoneNumber = "";

    // Add dashes after 3rd and 6th digits
    for (let i = 0; i < digitsOnly.length; i++) {
      if (i === 3 || i === 6) {
        formattedPhoneNumber += "-";
      }
      formattedPhoneNumber += digitsOnly[i];
    }

    // setFormattedPhone(formattedPhoneNumber); // Update the formatted phone in state
    contractorFormik.setValues((oldValue) => ({
      ...oldValue,
      mobile: formattedPhoneNumber,
    })); // Update formik field value
  };

  const license = currentUser?.user?.privateProvider_license;

  const initials = useMemo(
    () => ({
      firstName: license?.firstName ?? "",
      lastName: license?.lastName ?? "",
      businessName: license?.businessName ?? "",
      licenseType: license?.licenseType?.label ?? "",
      licenseNumber: license?.licenseNumber ?? "",
      rank: license?.licenseType?.code ?? "",
      status: "Active",
      expires: "",
      mainAddress:
        `${license?.address?.address1 || ""} ${license?.address?.address2 || ""} ${license?.address?.address3 || ""} ${license?.address?.city || ""}, ${license?.address?.state || ""} ${license?.address?.zip || ""}`
          .replace(/\s+/g, " ")
          .trim(),
      mainAddressCounty: license?.mainAddressCounty ?? "",
      address1: license?.address?.address1 ?? "",
      address2: license?.address?.address2 ?? "",
      address3: license?.address?.address3 ?? "",
      city: license?.address?.city ?? "",
      state: license?.address?.state ?? "",
      zip: license?.address?.zip ?? "",
      email: license?.email ?? "",
      mobile: license?.mobile ?? "",
    }),
    [license]
  );

  // CONTRACTOR FORMIK //
  const contractorFormik = useFormik({
    enableReinitialize: true,
    initialValues: initials,
    validationSchema: Yup.object({
      firstName: Yup.string(),
      lastName: Yup.string(),
      businessName: Yup.string(),
      mainAddress: Yup.string(),
      address1: Yup.string(),
      city: Yup.string(),
      state: Yup.string(),
      zip: Yup.string(),
      mainAddressCounty: Yup.string(),
      email: Yup.string(),
      mobile: Yup.string(),
    }),
    onSubmit: async (values) => {
      try {
        setAddContractorLoading(true);

        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/privateProvider/edit-license`,
          { currentSession: currentSession, values },
          { withCredentials: true }
        );

        if (resp?.data?.success) {
          await dispatch(fetchPrivateProviderData());
        }
      } catch (error) {
        console.error(error);
      } finally {
        setAddContractorLoading(false);
      }
    },
  });

  useEffect(() => {
    if (!license) return;
    contractorFormik.setValues(initials, false);
    contractorFormik.setTouched({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [license, initials]);

  useEffect(() => {
    // Update the mainAddress based on the individual address fields
    contractorFormik.setFieldValue(
      "mainAddress",
      `${contractorFormik?.values?.address1 || ""} ${
        contractorFormik?.values?.address2 || ""
      } ${contractorFormik?.values?.address3 || ""} ${
        contractorFormik?.values?.city || ""
      }, ${contractorFormik?.values?.state || ""} ${
        contractorFormik?.values?.zip || ""
      }`
        .replace(/\s+/g, " ") // Clean up extra spaces
        .trim()
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    contractorFormik?.values?.address1,
    contractorFormik?.values?.address2,
    contractorFormik?.values?.address3,
    contractorFormik?.values?.city,
    contractorFormik?.values?.state,
    contractorFormik?.values?.zip,
  ]);

  if (!currentSession || currentSession !== "privateProviderSession") {
    return <></>;
  } else {
    return (
      <FormikProvider value={contractorFormik}>
        <Card
          pb={3}
          pt={1}
          px={3}
          mb={4}
          component={Grid}
          sx={{
            width: tabletSize ? "90vw" : "80vw",
            maxWidth: "1100px",
            mx: "auto",
          }}
        >
          <Grid container>
            <Grid
              size={{ xs: 6 }}
              px={1}
              sx={{ borderRight: "6px solid #f3f3f3", my: "auto" }}
            >
              <ContractorCard
                data={contractorFormik?.values}
                colorIndex={2}
                empty={false}
                showLicenseInput={true}
                contractorSite={true}
                addNewActive={true}
                currentContractor={contractorFormik?.values}
              />
              <Box
                sx={{
                  pt: 0,
                  mt: -1,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{ cursor: "default" }}
                  align="center"
                  variant="caption"
                >
                  {contractorFormik?.values?.last_sync
                    ? convertIsoToLocalFormat(
                        contractorFormik?.values?.last_sync,
                        currentUser?.subuser
                          ? currentUser?.subuser?.timezone
                          : currentUser?.user?.personal?.[0]?.timeZone ||
                              "America/New_York",
                        "cccc, LLLL dd, yyyy hh:mm a"
                      )
                    : ""}
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  height: "100%",
                  position: "relative",
                }}
              >
                <Typography
                  color="primary"
                  className={"moveUp"}
                  sx={{
                    fontWeight: "bold",
                    position: "absolute",
                    top: "10px",
                    transition: "top 1s, font-size 1s", // Add font-size transition
                    fontSize: "2rem", // Corresponding to h4 and h3 sizes
                  }}
                >
                  My License
                </Typography>

                <Box
                  className={"fadeIn"}
                  sx={{ mt: 9, px: 1, transition: "fade 1s" }}
                >
                  <Grid spacing={1} container>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        disabled
                        size="small"
                        error={
                          !!(
                            contractorFormik.touched.firstName &&
                            contractorFormik.errors.firstName
                          )
                        }
                        helperText={
                          contractorFormik.touched.firstName &&
                          contractorFormik.errors.firstName
                        }
                        onBlur={contractorFormik.handleBlur}
                        onChange={contractorFormik.handleChange}
                        label="First Name"
                        name="firstName"
                        value={contractorFormik.values.firstName}
                        variant="filled"
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        disabled
                        size="small"
                        error={
                          !!(
                            contractorFormik.touched.lastName &&
                            contractorFormik.errors.lastName
                          )
                        }
                        helperText={
                          contractorFormik.touched.lastName &&
                          contractorFormik.errors.lastName
                        }
                        onBlur={contractorFormik.handleBlur}
                        onChange={contractorFormik.handleChange}
                        label="Last Name"
                        name="lastName"
                        value={contractorFormik.values.lastName}
                        variant="filled"
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        disabled={Boolean(license?.businessName)}
                        size="small"
                        error={
                          !!(
                            contractorFormik.touched.businessName &&
                            contractorFormik.errors.businessName
                          )
                        }
                        helperText={
                          contractorFormik.touched.businessName &&
                          contractorFormik.errors.businessName
                        }
                        onBlur={contractorFormik.handleBlur}
                        onChange={contractorFormik.handleChange}
                        label="Business Name"
                        name="businessName"
                        value={contractorFormik.values.businessName}
                        variant="filled"
                        fullWidth
                      />
                    </Grid>

                    <Grid size={{ xs: 6 }}>
                      <TextField
                        disabled={addContractorLoading}
                        size="small"
                        error={
                          !!(
                            contractorFormik.touched.address1 &&
                            contractorFormik.errors.address1
                          )
                        }
                        helperText={
                          contractorFormik.touched.address1 &&
                          contractorFormik.errors.address1
                        }
                        onBlur={contractorFormik.handleBlur}
                        onChange={contractorFormik.handleChange}
                        label="Address Line 1"
                        name="address1"
                        value={contractorFormik.values.address1}
                        variant="filled"
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <TextField
                        disabled={addContractorLoading}
                        size="small"
                        error={
                          !!(
                            contractorFormik.touched.address2 &&
                            contractorFormik.errors.address2
                          )
                        }
                        helperText={
                          contractorFormik.touched.address2 &&
                          contractorFormik.errors.address2
                        }
                        onBlur={contractorFormik.handleBlur}
                        onChange={contractorFormik.handleChange}
                        label="Address Line 2"
                        name="address2"
                        value={contractorFormik.values.address2}
                        variant="filled"
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <TextField
                        disabled={addContractorLoading}
                        size="small"
                        error={
                          !!(
                            contractorFormik.touched.address3 &&
                            contractorFormik.errors.address3
                          )
                        }
                        helperText={
                          contractorFormik.touched.address3 &&
                          contractorFormik.errors.address3
                        }
                        onBlur={contractorFormik.handleBlur}
                        onChange={contractorFormik.handleChange}
                        label="Address Line 3"
                        name="address3"
                        value={contractorFormik.values.address3}
                        variant="filled"
                        fullWidth
                      />
                    </Grid>

                    <Grid size={{ xs: 6 }}>
                      <TextField
                        disabled={addContractorLoading}
                        size="small"
                        error={
                          !!(
                            contractorFormik.touched.city &&
                            contractorFormik.errors.city
                          )
                        }
                        helperText={
                          contractorFormik.touched.city &&
                          contractorFormik.errors.city
                        }
                        onBlur={contractorFormik.handleBlur}
                        onChange={contractorFormik.handleChange}
                        label="City"
                        name="city"
                        value={contractorFormik.values.city}
                        variant="filled"
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 3 }}>
                      <TextField
                        disabled={addContractorLoading}
                        size="small"
                        error={
                          !!(
                            contractorFormik.touched.state &&
                            contractorFormik.errors.state
                          )
                        }
                        helperText={
                          contractorFormik.touched.state &&
                          contractorFormik.errors.state
                        }
                        onBlur={contractorFormik.handleBlur}
                        onChange={contractorFormik.handleChange}
                        label="State"
                        name="state"
                        value={contractorFormik.values.state}
                        variant="filled"
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 3 }}>
                      <TextField
                        disabled={addContractorLoading}
                        size="small"
                        error={
                          !!(
                            contractorFormik.touched.zip &&
                            contractorFormik.errors.zip
                          )
                        }
                        helperText={
                          contractorFormik.touched.zip &&
                          contractorFormik.errors.zip
                        }
                        onBlur={contractorFormik.handleBlur}
                        onChange={contractorFormik.handleChange}
                        label="Zip Code"
                        name="zip"
                        value={contractorFormik.values.zip}
                        variant="filled"
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Autocomplete
                        disabled={addContractorLoading}
                        sx={{ width: "100%" }}
                        options={countiesForLicense}
                        value={contractorFormik.values.mainAddressCounty} // This should match the selected value
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label="County"
                            error={Boolean(
                              contractorFormik.touched.mainAddressCounty &&
                                contractorFormik.errors.mainAddressCounty
                            )} // Show error if touched and error exists
                            helperText={
                              contractorFormik.touched.mainAddressCounty &&
                              contractorFormik.errors.mainAddressCounty
                            }
                          />
                        )}
                        onChange={(event, newValue) => {
                          // Manually update the form value
                          contractorFormik.setFieldValue(
                            "mainAddressCounty",
                            newValue
                          );
                        }}
                        ListboxProps={{
                          style: {
                            maxHeight: "150px",
                            overflowY: "auto",
                          },
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        disabled
                        size="small"
                        error={
                          !!(
                            contractorFormik.touched.email &&
                            contractorFormik.errors.email
                          )
                        }
                        helperText={
                          contractorFormik.touched.email &&
                          contractorFormik.errors.email
                        }
                        onBlur={contractorFormik.handleBlur}
                        onChange={contractorFormik.handleChange}
                        label="Email Address"
                        name="email"
                        value={contractorFormik.values.email}
                        variant="filled"
                        fullWidth
                      />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <TextField
                        disabled={addContractorLoading}
                        size="small"
                        error={
                          !!(
                            contractorFormik.touched.mobile &&
                            contractorFormik.errors.mobile
                          )
                        }
                        helperText={
                          contractorFormik.touched.mobile &&
                          contractorFormik.errors.mobile
                        }
                        onBlur={contractorFormik.handleBlur}
                        onChange={handlePhoneChange}
                        label="Mobile Phone"
                        name="mobile"
                        value={contractorFormik.values.mobile}
                        variant="filled"
                        fullWidth
                      />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Typography align="center" variant="h6" color="error">
                        {addContractorError}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }} sx={{ width: "100%" }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <LoadingButton
                          loading={addContractorLoading}
                          color="primary"
                          variant="contained"
                          onClick={() => {
                            setAddContractorError("");
                            contractorFormik.handleSubmit();
                          }}
                        >
                          UPDATE DETAILS
                        </LoadingButton>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Card>
      </FormikProvider>
    );
  }
};

export default LicenseInfoSection;
